import { Loader2, RefreshCw, Volume2, VolumeX, X, Zap } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { AudioWaveIndicator } from '@/shared/ui/audio-wave-indicator';
import { cn } from '@/shared/utils';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback } from 'react';

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const formattedMins = mins.toString().padStart(2, '0');
  const formattedSecs = secs.toString().padStart(2, '0');

  return hrs > 0
    ? `${hrs}:${formattedMins}:${formattedSecs}`
    : `${mins}:${formattedSecs}`;
};

export interface BingeLearningProps {
  open: boolean;
  title: string;
  progressPercent: number;
  duration: number;
  isMuted: boolean;
  isPlaying: boolean;
  currentContentIndex?: number;
  totalContents?: number;
  onClose: () => Promise<void> | void;
  onToggleMute: () => void;
  onPlay: () => void;
  onSync?: () => Promise<void> | void;
  isSyncing?: boolean;
}

export const BingeLearning = ({
  open,
  title,
  progressPercent,
  duration,
  isMuted,
  isPlaying,
  currentContentIndex,
  totalContents,
  onClose,
  onToggleMute,
  onPlay,
  onSync,
  isSyncing = false,
}: BingeLearningProps) => {
  const isProcessing = useIsMutating({
    mutationKey: ['update-server-playback-mode'],
  });
  const handleMuteToggle = useCallback(() => {
    if (!isPlaying) {
      onPlay();
    }
    onToggleMute();
  }, [isPlaying]);

  const handleClose = () => {
    if (isProcessing > 0) {
      return;
    }
    void onClose();
  };

  const clampedProgress = Math.min(Math.max(progressPercent, 0), 100);
  const formattedDuration = formatTime(duration);

  return (
    <div
      className={cn(
        ' inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-5 py-10',
        open ? 'fixed' : 'hidden',
      )}
      style={{
        paddingTop: 'max(2.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
        paddingLeft: 'max(1.25rem, env(safe-area-inset-left))',
        paddingRight: 'max(1.25rem, env(safe-area-inset-right))',
      }}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/20 p-8 text-white shadow-[0_24px_70px_rgba(15,23,42,0.45)]"
        style={{
          backgroundImage:
            'linear-gradient(154.035deg, rgb(139, 92, 246), rgb(59, 130, 246), rgb(99, 102, 241))',
        }}
      >
        <div className="absolute right-4 top-4 flex items-center gap-2">
          {/* {onSync && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void onSync()}
              disabled={isProcessing > 0 || isSyncing}
              className="h-9 w-9 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
              aria-label="Sync progress with server"
              title="Sync progress with server"
            >
              <RefreshCw
                className={cn('h-4 w-4', isSyncing && 'animate-spin')}
              />
            </Button>
          )} */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isProcessing > 0}
            className="h-9 w-9 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close binge overlay"
          >
            {isProcessing > 0 ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <X className="h-5 w-5" />
            )}
          </Button>
        </div>

        <header className="flex flex-col justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Zap
                className="size-5 text-amber-300"
                size={20}
                fill="currentColor"
              />
              <span className="text-white text-[15px] font-medium">
                Binge Learning
              </span>
            </div>
          </div>
          <h2 className="text-xl font-semibold leading-tight text-center mb-2">
            {title}
          </h2>
        </header>

        <div className="flex items-center gap-4 rounded-2xl justify-center mb-12">
          <div className="flex items-center gap-3 rounded-full  px-4 py-3 backdrop-blur-sm">
            <AudioWaveIndicator size="sm" className="scale-110" />
            <p className="text-sm font-semibold text-emerald-50">
              Playing continuously
            </p>
          </div>
        </div>
        {/* <section className="mt-10 space-y-6">
          <div className="space-y-3">
            <div className="flex justify-end">
              <span className="text-xs font-medium text-white/70">
                {clampedProgress}%
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/20">
              <div
                className="h-full rounded-full bg-amber-300 transition-all duration-500 ease-out"
                style={{ width: `${clampedProgress}%` }}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
              <span className="inline-flex h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-300 shadow-[0_0_12px_rgba(110,231,183,0.8)]" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white/90">
                  Playing continuously.
                </p>
                {currentContentIndex !== undefined &&
                  totalContents !== undefined && (
                    <p className="mt-1 text-xs text-white/70">
                      Contents {currentContentIndex} of {totalContents - 1}
                    </p>
                  )}
              </div>
            </div>
          </div>

          {import.meta.env.VITE_PUBLIC_APP_ENV === 'staging' && (
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/70">
              <div className="flex items-center justify-between">
                <span>Total duration</span>
                <span>{formattedDuration}</span>
              </div>
            </div>
          )}
        </section> */}

        <footer className="mt-4 flex flex-col items-center gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleMuteToggle}
            disabled={isProcessing > 0}
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 p-0 [&_svg]:size-6',
              isMuted &&
                'bg-emerald-400/20 text-emerald-100 hover:bg-emerald-400/30',
            )}
            aria-label={
              isMuted ? 'Unmute binge playback' : 'Mute binge playback'
            }
          >
            {isMuted ? (
              <VolumeX className="size-6" size={32} />
            ) : (
              <Volume2 className="size-6" size={32} />
            )}
          </Button>
          <div className="text-center text-xs text-white/70">
            Content will play continuously.
            <br />
            Exit binge mode to access full controls.
          </div>
        </footer>
      </div>
    </div>
  );
};
